/**
 * Settings → Geolocation
 * ──────────────────────
 * One-screen admin panel for the surveyor proximity-verification
 * feature. Configures:
 *   • the global soft-block threshold (metres) applied to sites that
 *     have no override,
 *   • the hard cap (metres) that ceilings both the global setting and
 *     any per-site override, and
 *   • whether the surveyor MUST supply a bypass reason before
 *     proceeding outside the threshold.
 *
 * Per-site overrides live on the Site edit form — this page shows a
 * hint pointing operators there. Changes take effect on the very next
 * public survey link resolve (no restart required).
 */
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { toast } from 'sonner';
import { MapPin, Save, ShieldCheck, AlertTriangle, Info, RefreshCcw, Loader2, CheckCircle2 } from 'lucide-react';

interface GeolocationConfig {
  enabled: boolean;
  global_threshold_m: number;
  hard_cap_m: number;
  require_bypass_reason: boolean;
  updated_at?: string;
}

const DEFAULT: GeolocationConfig = {
  enabled: true,
  global_threshold_m: 200,
  hard_cap_m: 2000,
  require_bypass_reason: true,
};

export const GeolocationSettings: React.FC = () => {
  const [cfg, setCfg] = useState<GeolocationConfig>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get<GeolocationConfig>('/settings/geolocation');
        setCfg({ ...DEFAULT, ...r.data });
      } catch (e) {
        // 404 or 403 → defaults render fine
        console.debug('geolocation settings load skipped', e);
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async (): Promise<void> => {
    // Guardrails: threshold must fit under the hard cap.
    if (cfg.global_threshold_m < 10) { toast.error('Threshold must be at least 10 m.'); return; }
    if (cfg.hard_cap_m < cfg.global_threshold_m) { toast.error('Hard cap must be greater than or equal to the threshold.'); return; }
    if (cfg.hard_cap_m > 100000) { toast.error('Hard cap cannot exceed 100 km.'); return; }
    setSaving(true);
    try {
      await api.put('/settings/geolocation', cfg);
      toast.success('Geolocation settings saved · applies to the next survey link resolve');
    } catch (err) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-raptor-navy/60" data-testid="geolocation-loading">Loading geolocation settings…</div>;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="geolocation-settings">
      <div>
        <h3 className="font-headline text-2xl text-raptor-navy tracking-wide">Survey Geolocation</h3>
        <p className="text-sm text-raptor-navy/60 font-body">Verifies the surveyor is on-site before a survey can be submitted. Uses browser GPS + the site's stored coordinates.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-raptor-blue" /> Proximity thresholds</CardTitle>
          <CardDescription>The global threshold applies to every site that doesn't have its own override on the Site detail page.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-md border border-border/60 bg-raptor-neutral/30">
            <div className="flex-1">
              <div className="font-headline text-raptor-navy text-sm">Enable geofence verification</div>
              <div className="text-xs text-raptor-navy/60 font-body">When off, surveys still capture the surveyor's coordinates but skip the distance check entirely.</div>
            </div>
            <button
              type="button" role="switch" aria-checked={cfg.enabled}
              onClick={() => setCfg({ ...cfg, enabled: !cfg.enabled })}
              data-testid="geolocation-enabled"
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cfg.enabled ? 'bg-raptor-blue' : 'bg-raptor-neutral'}`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${cfg.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Global soft-block threshold</Label>
              <div className="relative">
                <Input
                  type="number" min={10} max={cfg.hard_cap_m}
                  value={cfg.global_threshold_m}
                  onChange={(e) => setCfg({ ...cfg, global_threshold_m: Number(e.target.value) })}
                  data-testid="geolocation-threshold"
                  disabled={!cfg.enabled}
                  className="pr-10"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-raptor-navy/50 font-tabular pointer-events-none">m</span>
              </div>
              <div className="mt-1 text-[11px] text-raptor-navy/60 font-body">
                Surveyors within this radius pass silently. Beyond it, they see a warning dialog and must acknowledge / provide a reason to continue.
              </div>
            </div>
            <div>
              <Label>Hard cap (max configurable threshold)</Label>
              <div className="relative">
                <Input
                  type="number" min={cfg.global_threshold_m} max={100000}
                  value={cfg.hard_cap_m}
                  onChange={(e) => setCfg({ ...cfg, hard_cap_m: Number(e.target.value) })}
                  data-testid="geolocation-hard-cap"
                  disabled={!cfg.enabled}
                  className="pr-10"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-raptor-navy/50 font-tabular pointer-events-none">m</span>
              </div>
              <div className="mt-1 text-[11px] text-raptor-navy/60 font-body">
                Ceiling for every per-site override AND the point at which the survey app hard-blocks a submission (no bypass path). Keeps a runaway "1 000 000 m" typo from disabling the feature.
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 p-3 rounded-md border border-border/60">
            <div className="flex-1">
              <div className="font-headline text-raptor-navy text-sm">Require a bypass reason</div>
              <div className="text-xs text-raptor-navy/60 font-body">Forces the surveyor to type a short explanation before they can proceed outside the threshold. Recommended for audit-heavy programs.</div>
            </div>
            <button
              type="button" role="switch" aria-checked={cfg.require_bypass_reason}
              onClick={() => setCfg({ ...cfg, require_bypass_reason: !cfg.require_bypass_reason })}
              data-testid="geolocation-require-reason"
              disabled={!cfg.enabled}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${cfg.require_bypass_reason ? 'bg-raptor-blue' : 'bg-raptor-neutral'} ${!cfg.enabled ? 'opacity-40 cursor-not-allowed' : ''}`}
            >
              <span className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${cfg.require_bypass_reason ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
          </div>

          <div className="flex items-start gap-2 p-3 rounded-md border border-amber-200 bg-amber-50/60 text-xs text-amber-900 font-body">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              Geofence checks depend on the browser having permission to read GPS. Public survey links surface a soft-block dialog when a surveyor is outside the threshold — they can proceed with a reason, which is <b>permanently recorded on the survey record</b> and flagged in the Risk Ratings drawer.
            </div>
          </div>

          <div className="flex items-start gap-2 p-3 rounded-md border border-raptor-blue/30 bg-raptor-blue/5 text-xs text-raptor-navy font-body">
            <Info size={14} className="mt-0.5 shrink-0 text-raptor-blue" />
            <div>
              Need a bigger radius for an industrial estate, or a tighter radius for a single ATM cabinet? Set a <b>per-site override</b> on the Site edit form under Clients &amp; Sites. Overrides always respect the hard cap.
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button variant="gradient" onClick={save} disabled={saving} data-testid="geolocation-save">
              <Save size={14} /> {saving ? 'Saving…' : 'Save changes'}
            </Button>
            <div className="text-[11px] text-raptor-navy/50 font-body"><MapPin size={11} className="inline mr-0.5" /> Applies from the next survey link resolve.</div>
          </div>
        </CardContent>
      </Card>

      {/* ─── One-shot bulk regeocoder ────────────────────────
          Fixes historical sites whose lat/lng got frozen at import
          time (before auto-geocode-on-save landed) or whose address
          was edited without picking a Google Places suggestion. */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MapPin size={18} className="text-raptor-blue" /> Repair site coordinates</CardTitle>
          <CardDescription>
            Runs every site's <em>physical address</em> through the geocoder waterfall (Google → Mapbox → OpenStreetMap) and rewrites lat / lng where it moved. Use once after bulk imports or address corrections to make sure the Analytics map + Street View point at the right buildings.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RegeocodeSitesPanel />
        </CardContent>
      </Card>
    </div>
  );
};

export default GeolocationSettings;

/* ─── Bulk regeocoder — one-shot repair tool ─────────────────────
 * Kept in the same file (rather than a new component file) because
 * this is a one-time repair widget that lives only inside Geolocation
 * settings and reuses the same api client + toast pattern.
 */
interface RegeocodeReport {
  total_in_scope: number;
  processed: number;
  updated: number;
  unchanged: number;
  skipped_no_address: number;
  failed: number;
  next_offset: number | null;
  dry_run: boolean;
  providers_available: string[];
  diffs: Array<{
    site_id: string; name: string; address: string;
    before: { lat: number | null; lng: number | null };
    after: { lat: number; lng: number };
    formatted?: string; provider: string;
  }>;
}

const RegeocodeSitesPanel: React.FC = () => {
  const [force, setForce] = useState(false);
  const [running, setRunning] = useState(false);
  // Cumulative totals across pages so a 2 500-site run shows a
  // proper progress readout instead of the last batch only.
  const [totals, setTotals] = useState({ updated: 0, unchanged: 0, failed: 0, skipped: 0, processed: 0 });
  const [report, setReport] = useState<RegeocodeReport | null>(null);
  const [dryRunMode, setDryRunMode] = useState(true);

  const run = async (dry: boolean): Promise<void> => {
    setRunning(true);
    setDryRunMode(dry);
    setTotals({ updated: 0, unchanged: 0, failed: 0, skipped: 0, processed: 0 });
    setReport(null);
    try {
      let offset = 0;
      const pageSize = 200;
      let finalReport: RegeocodeReport | null = null;
      // Loop through pages until the backend tells us there's nothing left
      // (next_offset === null). Bounded at 100 pages = 20 000 sites so a
      // config error can never spin forever.
      for (let page = 0; page < 100; page += 1) {
        const r = await api.post<RegeocodeReport>(`/sites/regeocode-batch?limit=${pageSize}&offset=${offset}&force=${force}&dry_run=${dry}`);
        finalReport = r.data;
        setTotals((t) => ({
          updated: t.updated + r.data.updated,
          unchanged: t.unchanged + r.data.unchanged,
          failed: t.failed + r.data.failed,
          skipped: t.skipped + r.data.skipped_no_address,
          processed: t.processed + r.data.processed,
        }));
        if (r.data.next_offset === null) break;
        offset = r.data.next_offset;
      }
      setReport(finalReport);
      toast.success(dry
        ? `Dry run complete · ${finalReport?.updated ?? 0} site(s) would move`
        : `Regeocoded ${finalReport?.updated ?? 0} site(s) · refresh Analytics to see the moved markers`);
    } catch (err) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Regeocode run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="regeocode-panel">
      <div className="flex items-center gap-3 p-3 rounded-md border border-border/60 bg-raptor-neutral/20">
        <input
          id="regeocode-force"
          type="checkbox"
          checked={force}
          onChange={(e) => setForce(e.target.checked)}
          disabled={running}
          data-testid="regeocode-force"
          className="h-4 w-4"
        />
        <label htmlFor="regeocode-force" className="flex-1 cursor-pointer">
          <div className="font-headline text-raptor-navy text-sm">Include sites that already have coordinates</div>
          <div className="text-xs text-raptor-navy/60 font-body">
            Turn this on if you've corrected addresses on sites whose old coords are still stale. When off, only sites missing coordinates get processed.
          </div>
        </label>
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => void run(true)}
          disabled={running}
          data-testid="regeocode-dry-run"
        >
          {running && dryRunMode ? <Loader2 size={14} className="animate-spin mr-1" /> : <RefreshCcw size={14} className="mr-1" />}
          Preview changes (dry run)
        </Button>
        <Button
          variant="gradient"
          onClick={() => void run(false)}
          disabled={running}
          data-testid="regeocode-apply"
        >
          {running && !dryRunMode ? <Loader2 size={14} className="animate-spin mr-1" /> : <MapPin size={14} className="mr-1" />}
          {running && !dryRunMode ? 'Regeocoding…' : 'Apply — regeocode now'}
        </Button>
      </div>

      {(running || totals.processed > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
          <StatCell label="Processed" value={totals.processed} />
          <StatCell label={dryRunMode ? 'Would move' : 'Moved'} value={totals.updated} tone="good" />
          <StatCell label="Unchanged" value={totals.unchanged} />
          <StatCell label="No address" value={totals.skipped} tone={totals.skipped ? 'warn' : undefined} />
          <StatCell label="Failed" value={totals.failed} tone={totals.failed ? 'bad' : undefined} />
        </div>
      )}

      {report && !running && (
        <div className="text-xs text-raptor-navy/70 font-body">
          Providers used in priority order: <b>{report.providers_available.join(' → ') || 'none'}</b>.
          {!report.providers_available.includes('google_maps') && (
            <span className="text-amber-700"> Google Maps not connected — results using OpenStreetMap will be less precise.</span>
          )}
        </div>
      )}

      {report && report.diffs.length > 0 && (
        <div className="rounded-md border border-border/60 overflow-hidden">
          <div className="bg-raptor-neutral/40 px-3 py-1.5 text-[10px] uppercase tracking-wide text-raptor-navy/70 flex items-center justify-between">
            <span>{report.diffs.length} site(s) {dryRunMode ? 'would be moved' : 'moved'}</span>
            <span className="font-tabular">showing first 25</span>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-border/40">
            {report.diffs.slice(0, 25).map((d) => (
              <div key={d.site_id} className="px-3 py-2 text-xs">
                <div className="font-headline text-raptor-navy truncate">{d.name}</div>
                <div className="text-raptor-navy/60 truncate">{d.address}</div>
                <div className="mt-1 flex items-center gap-2 font-tabular text-[11px]">
                  <span className="text-raptor-navy/50">
                    {d.before.lat != null ? `${d.before.lat.toFixed(5)}, ${d.before.lng!.toFixed(5)}` : '—'}
                  </span>
                  <span className="text-raptor-navy/40">→</span>
                  <span className="text-emerald-700">
                    {d.after.lat.toFixed(5)}, {d.after.lng.toFixed(5)}
                  </span>
                  <span className="ml-auto text-raptor-navy/40 text-[10px] uppercase">{d.provider.replace('_', ' ')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!running && !report && (
        <div className="text-[11px] text-raptor-navy/50 font-body flex items-start gap-1.5">
          <Info size={11} className="mt-0.5 shrink-0" />
          Recommended flow: run a <b>Preview</b> first to see what would move, then <b>Apply</b> once the report looks right. Safe to re-run — sites that don't move are skipped.
        </div>
      )}
    </div>
  );
};

const StatCell: React.FC<{ label: string; value: number; tone?: 'good' | 'warn' | 'bad' }> = ({ label, value, tone }) => (
  <div className={`rounded-md border p-2 ${
    tone === 'good' ? 'border-emerald-300 bg-emerald-50/50'
    : tone === 'warn' ? 'border-amber-300 bg-amber-50/50'
    : tone === 'bad' ? 'border-red-300 bg-red-50/50'
    : 'border-border/60'
  }`}>
    <div className="text-[9px] uppercase tracking-wide text-raptor-navy/60">{label}</div>
    <div className="font-tabular font-semibold text-raptor-navy">{value.toLocaleString()}</div>
  </div>
);
